// Row mappers for the staffing configuration tables (migration
// 20260729120100_staffing_profiles.sql) → the phase-1 pure core.
//
// Same convention as boardRows.ts: PostgREST returns snake_case, the SPAs read
// these tables directly under RLS, and the mapping happens here exactly once so
// every surface agrees.
//
// This is the seam between phase 2 (storage) and phase 1 (model). Nothing in
// staffingModel.ts knows a database exists, and nothing here computes anything.

import type { DemandFormula } from './boardCoverage.js';
import { DEMAND_METRICS } from './boardCoverage.js';
import {
  DEFAULT_MAX_HEADS,
  DEFAULT_ROOM_MINUTES,
  type BinarySpec,
  type RoomEventMinutes,
  type StaffingSpec,
  type TieredBand,
  type TieredPost,
  type TieredSpec,
  type WorkContentSpec,
} from './staffingModel.js';

// ── Row shapes ──────────────────────────────────────────────────────────────

export interface StaffingProfileRow {
  id: number;
  label: string;
  archetype: string;
  demand_formula: Record<string, number> | null;
  active: boolean;
  sort: number;
  note: string | null;
}

export interface StaffingBandRow {
  id: number;
  profile_id: number;
  from_demand: number;
}

export interface StaffingPostRow {
  id: number;
  band_id: number;
  activity_id: number | null;
  label: string;
  window_start: string;
  window_end: string;
  heads: number;
  sort: number;
}

export interface WorkContentRow {
  profile_id: number;
  minutes_a_blanc: number;
  minutes_recouche: number;
  minutes_recouche_fond: number;
  minutes_arrivee: number;
  comfort_floor_equivalents: number;
  deep_clean_cadence: number;
  earliest_start: string;
  default_start: string;
  latest_end: string;
  heavy_arrivals: number;
  check_in_early: string;
  check_in_normal: string;
  /** Heads/time trade-off bounds (ADR-0015). Absent on rows read before that migration. */
  max_heads?: number;
  target_finish?: string | null;
}

export interface CombinationRow {
  activity_a: number;
  activity_b: number;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Postgres `time` comes back as 'HH:MM:SS'; the model compares 'HH:MM' strings
 * lexicographically. Normalising here rather than making every comparison
 * length-tolerant keeps the model's string ordering sound.
 */
export function hhmm(value: string): string {
  return value.slice(0, 5);
}

/** Drop unknown keys and non-numeric values — the DB CHECK guards writes, this guards reads. */
export function formulaFromDb(value: Record<string, number> | null): DemandFormula {
  const formula: DemandFormula = {};
  if (!value) return formula;
  for (const metric of DEMAND_METRICS) {
    const coefficient = value[metric];
    if (typeof coefficient === 'number' && Number.isFinite(coefficient)) formula[metric] = coefficient;
  }
  return formula;
}

export function roomMinutesFromDb(row: WorkContentRow): RoomEventMinutes {
  return {
    aBlanc: row.minutes_a_blanc,
    recouche: row.minutes_recouche,
    recoucheAFond: row.minutes_recouche_fond,
    arrivee: row.minutes_arrivee,
  };
}

/** The unordered pair keys the allocator checks concurrency against. */
export function combinationsFromDb(rows: CombinationRow[]): Set<string> {
  return new Set(rows.map((r) => (r.activity_a < r.activity_b ? `${r.activity_a}:${r.activity_b}` : `${r.activity_b}:${r.activity_a}`)));
}

// ── Profile assembly ────────────────────────────────────────────────────────

export interface StaffingConfigRows {
  profiles: StaffingProfileRow[];
  bands: StaffingBandRow[];
  posts: StaffingPostRow[];
  workContent: WorkContentRow[];
}

function postFromRow(row: StaffingPostRow): TieredPost {
  return {
    activityId: row.activity_id,
    label: row.label,
    windowStart: hhmm(row.window_start),
    windowEnd: hhmm(row.window_end),
    heads: row.heads,
  };
}

/**
 * Assemble stored rows into the specs the model consumes.
 *
 * Malformed configuration is SKIPPED rather than thrown on, and reported. A
 * profile the operator has half-finished in the editor must not take down the
 * whole board — the correct failure mode for a staffing config is « that
 * activity has no requirement today », which the coverage view already knows how
 * to display, not a blank page.
 */
export interface StaffingConfig {
  specs: StaffingSpec[];
  /** Profiles that could not be assembled, with why — surfaced in the editor. */
  problems: { profileId: number; label: string; reason: string }[];
}

export function staffingConfigFromRows(rows: StaffingConfigRows): StaffingConfig {
  const bandsByProfile = new Map<number, StaffingBandRow[]>();
  for (const band of rows.bands) {
    bandsByProfile.set(band.profile_id, [...(bandsByProfile.get(band.profile_id) ?? []), band]);
  }
  const postsByBand = new Map<number, StaffingPostRow[]>();
  for (const post of rows.posts) {
    postsByBand.set(post.band_id, [...(postsByBand.get(post.band_id) ?? []), post]);
  }
  const workByProfile = new Map(rows.workContent.map((w) => [w.profile_id, w]));

  const specs: StaffingSpec[] = [];
  const problems: StaffingConfig['problems'] = [];

  for (const profile of [...rows.profiles].sort((a, b) => a.sort - b.sort || a.id - b.id)) {
    if (!profile.active) continue;

    const bands = (bandsByProfile.get(profile.id) ?? []).sort((a, b) => a.from_demand - b.from_demand);

    if (profile.archetype === 'workContent') {
      const work = workByProfile.get(profile.id);
      if (!work) {
        problems.push({ profileId: profile.id, label: profile.label, reason: 'work-content profile has no parameter row' });
        continue;
      }
      // The single post carries the activity identity; the parameters carry the
      // window bounds, so the post's own window is unused (the window is
      // DERIVED per day, brief §4.4).
      const anchor = bands.flatMap((b) => postsByBand.get(b.id) ?? [])[0];
      if (!anchor || anchor.activity_id === null) {
        problems.push({ profileId: profile.id, label: profile.label, reason: 'work-content profile has no anchor activity' });
        continue;
      }
      const spec: WorkContentSpec = {
        kind: 'workContent',
        activityId: anchor.activity_id,
        profileId: profile.id,
        label: profile.label,
        minutes: roomMinutesFromDb(work),
        comfortFloorThreshold: work.comfort_floor_equivalents,
        earliestStart: hhmm(work.earliest_start),
        defaultStart: hhmm(work.default_start),
        latestEnd: hhmm(work.latest_end),
        maxHeads: work.max_heads ?? DEFAULT_MAX_HEADS,
        // Null/absent stays UNDEFINED, never a default time: an accidental
        // target would silently add people, and « no target » is the historic
        // behaviour this must reproduce bit-for-bit.
        ...(work.target_finish ? { targetFinish: hhmm(work.target_finish) } : {}),
      };
      specs.push(spec);
      continue;
    }

    if (bands.length === 0) {
      problems.push({ profileId: profile.id, label: profile.label, reason: 'profile has no band' });
      continue;
    }

    if (profile.archetype === 'binary') {
      // A binary profile is one band (its threshold) holding one post. Stored
      // in the same shape as a tiered profile so the editor and the RLS story
      // stay uniform; collapsed here because the model's BinarySpec says what
      // it means far more plainly than a one-band table would.
      const band = bands[bands.length - 1] as StaffingBandRow;
      const post = (postsByBand.get(band.id) ?? [])[0];
      if (!post || post.activity_id === null) {
        problems.push({ profileId: profile.id, label: profile.label, reason: 'binary profile has no post with an activity' });
        continue;
      }
      const spec: BinarySpec = {
        kind: 'binary',
        activityId: post.activity_id,
        label: post.label,
        windowStart: hhmm(post.window_start),
        windowEnd: hhmm(post.window_end),
        formula: formulaFromDb(profile.demand_formula),
        threshold: band.from_demand,
      };
      specs.push(spec);
      continue;
    }

    const tieredBands: TieredBand[] = [];
    for (const band of bands) {
      const posts = (postsByBand.get(band.id) ?? []).sort((a, b) => a.sort - b.sort || a.id - b.id);
      if (posts.length === 0) continue;
      tieredBands.push({ from: band.from_demand, posts: posts.map(postFromRow) });
    }
    if (tieredBands.length === 0) {
      problems.push({ profileId: profile.id, label: profile.label, reason: 'tiered profile has no band with posts' });
      continue;
    }
    const spec: TieredSpec = {
      kind: 'tiered',
      label: profile.label,
      formula: formulaFromDb(profile.demand_formula),
      bands: tieredBands,
    };
    specs.push(spec);
  }

  return { specs, problems };
}

/** Default room minutes as a row, for seeding an editor form. */
export function defaultWorkContentRow(profileId: number): WorkContentRow {
  return {
    profile_id: profileId,
    minutes_a_blanc: DEFAULT_ROOM_MINUTES.aBlanc,
    minutes_recouche: DEFAULT_ROOM_MINUTES.recouche,
    minutes_recouche_fond: DEFAULT_ROOM_MINUTES.recoucheAFond,
    minutes_arrivee: DEFAULT_ROOM_MINUTES.arrivee,
    comfort_floor_equivalents: 10,
    deep_clean_cadence: 4,
    earliest_start: '08:00:00',
    default_start: '09:00:00',
    latest_end: '17:00:00',
    heavy_arrivals: 15,
    check_in_early: '14:00:00',
    check_in_normal: '15:00:00',
    max_heads: DEFAULT_MAX_HEADS,
    target_finish: null,
  };
}
